import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ClickableAvatar } from "./ClickableAvatar";
import { TrendingUp, Scale, Ruler, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const MEASUREMENT_INFO = {
  weight: { label: "Weight", unit: "kg", icon: Scale },
  chest: { label: "Chest", unit: "cm", icon: Ruler },
  waist: { label: "Waist", unit: "cm", icon: Ruler },
  arm: { label: "Arm", unit: "cm", icon: Ruler },
  bmi: { label: "BMI", unit: "", icon: Scale },
};

export function AddProgressModal({
  open,
  onOpenChange,
  goal,
  existingEntry,
  selectedDate,
  member,
  branchId,
  onSuccess,
}) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [measurements, setMeasurements] = useState([]);
  const [notes, setNotes] = useState("");
  const [recordDate, setRecordDate] = useState("");
  const [recordTime, setRecordTime] = useState("");
  const [completeGoal, setCompleteGoal] = useState(true);

  useEffect(() => {
    if (goal && open) {
      const initialMeasurements =
        goal.measurements?.map((m) => {
          // If editing an existing entry, use its measurement values
          if (existingEntry?.measurements) {
            const existing = existingEntry.measurements.find(
              (em) => em.metric === m.metric,
            );
            if (existing) {
              return {
                metric: m.metric,
                value: existing.value,
                targetValue: m.targetValue,
                startValue: m.currentValue,
              };
            }
          }
          // Otherwise use latest progress or goal's current value
          let latestValue = m.currentValue;
          if (goal.latestProgress?.measurements) {
            const latestM = goal.latestProgress.measurements.find(
              (pm) => pm.metric === m.metric,
            );
            if (latestM) latestValue = latestM.value;
          }
          return {
            metric: m.metric,
            value: latestValue,
            targetValue: m.targetValue,
            startValue: m.currentValue,
          };
        }) || [];
      setMeasurements(initialMeasurements);
      // Pre-fill notes from existing entry if editing
      setNotes(existingEntry?.notes || "");

      // Pre-fill date and time
      if (existingEntry?.recordedDate) {
        const rawDate = existingEntry.recordedDate;
        setRecordDate(rawDate.slice(0, 10));
        if (rawDate.includes("T")) {
          setRecordTime(rawDate.split("T")[1].slice(0, 5));
        } else {
          setRecordTime(format(new Date(), "HH:mm"));
        }
      } else {
        const prefillDate = selectedDate ? new Date(selectedDate) : new Date();
        setRecordDate(format(prefillDate, "yyyy-MM-dd"));
        setRecordTime(format(prefillDate, "HH:mm"));
      }
    }
  }, [goal, open, existingEntry, selectedDate]);

  const updateMeasurement = (metric, value) => {
    setMeasurements(
      measurements.map((m) =>
        m.metric === metric ? { ...m, value: parseFloat(value) || 0 } : m,
      ),
    );
  };

  const calculateProgress = (m) => {
    const { startValue, targetValue, value } = m;
    if (targetValue === startValue) return 0;

    let progress;
    if (goal?.goalType === "Weight Loss") {
      progress = ((startValue - value) / (startValue - targetValue)) * 100;
    } else {
      progress = ((value - startValue) / (targetValue - startValue)) * 100;
    }
    return Math.max(0, Math.min(100, progress));
  };

  const overallProgressPercent = (() => {
    if (!measurements || measurements.length === 0) return 0;
    let totalProgress = 0;
    let validCount = 0;
    measurements.forEach((m) => {
      if (m.targetValue !== m.startValue) {
        totalProgress += calculateProgress(m);
        validCount += 1;
      }
    });
    return validCount > 0 ? Math.round(totalProgress / validCount) : 0;
  })();

  useEffect(() => {
    if (overallProgressPercent >= 100) {
      setCompleteGoal(true);
    }
  }, [overallProgressPercent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formattedDate = `${recordDate}T${recordTime || "12:00"}:00`;
      const hasReached100 = overallProgressPercent >= 100;
      const isGoalActive = goal.status === "Active";
      const shouldComplete = hasReached100 && isGoalActive && completeGoal;

      if (existingEntry?.id) {
        // Update existing progress entry
        await api.put(`/progress/${existingEntry.id}`, {
          measurements: measurements.map((m) => ({
            metric: m.metric,
            value: m.value,
          })),
          notes,
          recordedDate: formattedDate,
          completeGoal: shouldComplete,
        });
        toast.success("Progress updated successfully");
      } else {
        // Create new progress entry
        await api.post("/progress", {
          goalId: goal.id,
          memberId: goal.memberId,
          branchId: branchId || goal.branchId || "",
          measurements: measurements.map((m) => ({
            metric: m.metric,
            value: m.value,
          })),
          notes,
          recordedDate: formattedDate,
          completeGoal: shouldComplete,
        });
        toast.success("Progress recorded successfully");
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save progress");
    } finally {
      setLoading(false);
    }
  };

  const memberDetails = member || goal?.member;
  const photoSrc = memberDetails?.photo
    ? memberDetails.photo.startsWith("http")
      ? memberDetails.photo
      : `${BACKEND_URL}${memberDetails.photo.startsWith("/") ? "" : "/"}${memberDetails.photo}`
    : null;

  if (!goal) return null;

  const isEditing = !!existingEntry?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="add-progress-modal"
      >
        <DialogHeader className="pb-2 border-b border-border shrink-0">
          <DialogTitle className="text-lg font-heading flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            {isEditing ? "Edit Progress" : "Record Progress"}
          </DialogTitle>
          <DialogDescription className="flex flex-col gap-1 mt-1">
            <div>
              {isEditing ? "Update" : "Add"} measurements for:{" "}
              <span className="text-foreground font-medium">{goal.name}</span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto space-y-2 md:space-y-3 pb-4"
        >
          {/* Member Info */}
          {memberDetails && (
            <div className="flex items-center gap-3 p-2 bg-secondary/30 rounded-xl">
              <ClickableAvatar
                src={photoSrc}
                name={memberDetails.name}
                className="h-12 w-12 rounded-xl border border-border shrink-0"
                fallbackClassName="rounded-xl bg-primary/15 text-primary font-heading text-lg"
              />
              <div>
                <p className="font-semibold">{memberDetails.name}</p>
                <p className="text-sm text-muted-foreground font-mono">
                  {memberDetails.phone}
                </p>
              </div>
            </div>
          )}

          {/* Goal Summary */}
          <div className="p-3 bg-secondary/30 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <Badge variant="outline" className="text-xs font-mono">
                {goal.goalType}
              </Badge>
              <Badge
                className={`text-xs font-mono ${
                  goal.status === "Active"
                    ? "bg-emerald-500/15 text-emerald-500"
                    : goal.status === "Completed"
                      ? "bg-blue-500/15 text-blue-500"
                      : "bg-amber-500/15 text-amber-500"
                }`}
              >
                {goal.status}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Progress
                value={goal.progressPercent || 0}
                className="flex-1 h-2"
              />
              <span className="text-sm font-mono text-muted-foreground">
                {goal.progressPercent || 0}%
              </span>
            </div>
          </div>

          {/* Measurements */}
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
              {isEditing ? "Edit Measurements" : "Update Measurements"}
            </Label>

            {measurements.map((m) => {
              const info = MEASUREMENT_INFO[m.metric] || {
                label: m.metric,
                unit: "",
                icon: Scale,
              };
              const Icon = info.icon;
              const progress = calculateProgress(m);

              return (
                <div
                  key={m.metric}
                  className="p-4 bg-secondary/20 rounded-lg space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary" />
                      <span className="font-medium text-sm">{info.label}</span>
                      {info.unit && (
                        <span className="text-xs text-muted-foreground">
                          ({info.unit})
                        </span>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs font-mono">
                      {Math.round(progress)}%
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex-1 text-center">
                      <p className="text-[10px] text-muted-foreground font-mono">
                        START
                      </p>
                      <p className="text-sm font-mono">{m.startValue}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="text-[10px] text-muted-foreground font-mono">
                        CURRENT
                      </p>
                      <Input
                        type="number"
                        step="0.1"
                        value={m.value}
                        onChange={(e) =>
                          updateMeasurement(m.metric, e.target.value)
                        }
                        className="h-9 text-center font-mono"
                        data-testid={`progress-${m.metric}-input`}
                      />
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 text-center">
                      <p className="text-[10px] text-muted-foreground font-mono">
                        TARGET
                      </p>
                      <p className="text-sm font-mono text-primary">
                        {m.targetValue}
                      </p>
                    </div>
                  </div>

                  <Progress value={progress} className="h-1.5" />
                </div>
              );
            })}
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                Date
              </Label>
              <Input
                type="date"
                value={recordDate}
                onChange={(e) => setRecordDate(e.target.value)}
                className="h-9 font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                Time
              </Label>
              <Input
                type="time"
                value={recordTime}
                onChange={(e) => setRecordTime(e.target.value)}
                className="h-9 font-mono"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
              Notes (Optional)
            </Label>
            <Textarea
              placeholder="Add any notes about today's progress..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[60px]"
            />
          </div>

          {/* Goal Completion Checkbox */}
          {overallProgressPercent >= 100 && goal.status === "Active" && (
            <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl animate-in fade-in slide-in-from-bottom-2 duration-300">
              <Checkbox
                id="complete-goal-checkbox"
                checked={completeGoal}
                onCheckedChange={(checked) => setCompleteGoal(checked)}
                className="border-emerald-500/50 data-[state=checked]:bg-emerald-500 data-[state=checked]:text-white"
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="complete-goal-checkbox"
                  className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 cursor-pointer"
                >
                  Goal Completed!
                </label>
                <p className="text-xs text-emerald-600/80 dark:text-emerald-400/80">
                  Mark this goal as completed
                </p>
              </div>
            </div>
          )}
        </form>

        <div className="pt-4 border-t border-border shrink-0">
          <Button
            type="submit"
            className="w-full h-11 font-heading uppercase tracking-wider"
            onClick={handleSubmit}
            disabled={loading}
            data-testid="save-progress-btn"
          >
            {loading
              ? "Saving..."
              : isEditing
                ? "Update Progress"
                : "Record Progress"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
